// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { Spinner } from '../../../common/component/Spinner'
import { IAssetDto, IAssetNestedDto } from '../Asset.model'
import { useAssetGetByIdMutation } from '../Asset.api'
import { IconLabelContent } from '../../../common/component/IconLabelContent'
import { ContractEmbedered } from '../../contract/components/ContractEmbedered'
import { useAppSelector } from '../../store/Store'
import { selectAsset } from '../Asset.slice'

interface Props {
  asset: IAssetNestedDto
}

export const AssetEmbedered: FC<Props> = ({asset}: Props) => {
  const intl = useIntl()
  const storeData = useAppSelector(selectAsset)
  const didFetchData = useRef(false)  
  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<IAssetDto>()  
  const [getById] = useAssetGetByIdMutation()

  useEffect(() => {
    const fetchData = async (id) => {
      try {
        if (!didFetchData.current) {
          const response: any = await getById({id})
          if (!response || response.error) {
            setError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            setData(response.data)
          }
        }
      } catch (e: any) {
        setError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsLoading(false)
      }
    }
    if (storeData && storeData.id === asset.id) {      
      setData(storeData)
      setIsLoading(false)
    } else {
      fetchData(asset.id)
    }
  }, [getById, asset, intl, storeData])

  const buildClientDescriptionLine = (data: IAssetDto) => {
    let description = `${data.client.displayName}`
    
    if (data.address) {
      description += description ? ` | ${data.address}` : `${data.address}`
    }
    return description
  }

  const buildAssetDescriptionLine = (data: IAssetDto) => {
    return `${data.family} | ${data.area} | ${data.brand} | ${data.model} | ${data.serial}`
  }

  return (
    <>
      <CustomInputRenderer
        label={intl.formatMessage({id: 'UTIL.ASSET'})}
        object={
          !data && isLoading ? (
            <Spinner />
          ) : data ? (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='fs-7 cursor-pointer'>
                      <Link className='' to={`/assets/${data.id}`} target='_blank'>
                        <span className='fw-bolder mb-2'>{data.displayName}</span>
                        <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                        <span className='text-primary mb-2'>{data.sequence}</span>
                      </Link>
                    </div>

                    <div className='d-flex justify-content-start'>
                      <div className='d-flex align-items-center fs-8'>
                        <span className='text-gray-600'>{buildClientDescriptionLine(data)}</span>
                      </div>
                    </div>

                    <div className='d-flex justify-content-start'>
                      <div className='d-flex align-items-center fs-8'>
                        <span className='text-gray-600'>{buildAssetDescriptionLine(data)}</span>
                      </div>
                    </div>

                    <div className='d-flex justify-content-start align-items-center'>
                      <IconLabelContent
                        title={
                          data.contract
                            ? `${intl.formatMessage({id: 'UTIL.CONTRACT'})} ${
                                data.contract.sequence
                              }`
                            : ''
                        }
                        active={!!data.contract}
                        icon='bi bi-vector-pen'
                      />

                      <IconLabelContent
                        title={`${intl
                          .formatMessage({id: 'TITLE.REPORT.TOTAL'})
                          .replace(/%s/g, `${data.totalReport > 0 ? data.totalReport : 0}`)}`}
                        labelValue={`${data.totalReport || 0}`}
                        active={data.totalReport > 0}
                        icon='bi bi-speedometer'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='fs-7'>
                      <span className='fw-bolder mb-2'>{asset.displayName}</span>
                      <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                      <span className='text-primary mb-2'>{asset.sequence}</span>
                    </div>
                    {error && (
                      <span className='text-danger '>
                        <div className='d-flex flex-row justify-content-start align-items-center'>
                          <span className='fs-8 me-1'>
                            <i className='text-danger me-1 bi bi-exclamation-octagon' />
                            {error}
                          </span>
                        </div>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ></CustomInputRenderer>
      {data && data.contract && <ContractEmbedered contract={data.contract} />}
    </>
  )
}
